import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import FadeInSection from '@/components/base/FadeInSection';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { openEnquiry } = useEnquiryModal();

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.append('access_key', '4a52e82c-e17c-43dd-8725-223c6bfec93e');

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        form.reset();
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <InnerPageHero
          bgImage="/images/contact-hero.jpg"
          bgAlt="Contact Bassi Enterprises - Fresh produce supply"
          bgTitle="Contact Bassi Enterprises - Fresh Fruits & Vegetables Supplier"
          breadcrumbLabel="Contact"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-customer-service-2-line"></i>
              Get In Touch
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Contact Bassi Enterprises
            </h1>
            <p className="text-background-50/50 text-sm md:text-base font-semibold mb-4 tracking-wide uppercase">
              Let&rsquo;s discuss your fresh produce supply requirements
            </p>
            <p className="text-background-200 text-base md:text-lg leading-relaxed mb-8">
              Whether you need regular supply or bulk quantities, our team is ready to help. Reach out and we&rsquo;ll get back to you within 24 hours.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {[
                { icon: 'ri-shield-check-line', label: 'FSSAI Registered' },
                { icon: 'ri-government-line', label: 'MSME Registered' },
                { icon: 'ri-bill-line', label: 'GST Registered' },
              ].map((badge) => (
                <span
                  key={badge.label}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-background-50/10 backdrop-blur-sm text-background-50 rounded-full text-sm font-medium border border-background-50/15"
                >
                  <i className={`${badge.icon} text-primary-400 text-sm`}></i>
                  {badge.label}
                </span>
              ))}
            </div>
          </div>
        </InnerPageHero>

        {/* Contact Content */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <FadeInSection>
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
                <div className="lg:col-span-2">
                  <div className="bg-background-100 rounded-2xl p-8 md:p-10 sticky top-24">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                      Bassi Enterprises
                    </h2>
                    <p className="text-foreground-500 text-sm mb-8">
                      Fresh Fruits & Vegetables Supplier
                    </p>

                    <div className="space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-2-line text-primary-600 text-lg"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-foreground-900 mb-1">Address</h4>
                          <p className="text-foreground-500 text-sm leading-relaxed">
                            248, Village Haiderpur, Shamli-Panipat Marg, Kairana,<br />
                            Shamli, Uttar Pradesh, India<br />
                            PIN: 247774
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-phone-line text-primary-600 text-lg"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-foreground-900 mb-1">Phone</h4>
                          <a
                            href="tel:+919084756574"
                            className="text-foreground-500 text-sm hover:text-primary-600 transition-colors"
                          >
                            +91 9084756574
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-primary-600 text-lg"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-foreground-900 mb-1">Email</h4>
                          <a
                            href="mailto:contact@bassifresh.com"
                            className="text-foreground-500 text-sm hover:text-primary-600 transition-colors break-all"
                          >
                            contact@bassifresh.com
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 space-y-3">
                      <a
                        href="tel:+919084756574"
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-phone-fill text-lg"></i>
                        Call Now
                      </a>
                      <button
                        onClick={() => openEnquiry()}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-accent-500 hover:bg-accent-400 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-whatsapp-fill text-lg"></i>
                        WhatsApp
                      </button>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <div className="bg-background-50 border border-background-200 rounded-2xl p-8 md:p-10">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">
                      Send Inquiry
                    </h2>
                    <p className="text-foreground-500 text-sm mb-7">
                      Fill in the details below and our team will respond within 24 hours.
                    </p>

                    {submitted ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                          <i className="ri-check-line text-primary-600 text-2xl"></i>
                        </div>
                        <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                          Inquiry Sent Successfully!
                        </h3>
                        <p className="text-foreground-500 text-sm max-w-sm mx-auto">
                          Thank you for reaching out. Our team will contact you within 24 hours.
                        </p>
                        <button
                          onClick={() => setSubmitted(false)}
                          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          Send Another Inquiry
                        </button>
                      </div>
                    ) : (
                      <form
                        data-readdy-form
                        onSubmit={handleSubmit}
                        className="space-y-5"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              required
                              placeholder="Enter your full name"
                              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                            />
                          </div>
                          <div>
                            <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              Company Name
                            </label>
                            <input
                              type="text"
                              id="company"
                              name="company"
                              placeholder="Enter your company name"
                              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                            />
                          </div>
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              Phone Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              required
                              placeholder="Enter your phone number"
                              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                            />
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              required
                              placeholder="Enter your email address"
                              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <label htmlFor="products" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              Required Products
                            </label>
                            <input
                              type="text"
                              id="products"
                              name="products"
                              placeholder="e.g., Potatoes, Tomatoes, Onions"
                              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                            />
                          </div>
                          <div>
                            <label htmlFor="quantity" className="block text-sm font-medium text-foreground-700 mb-1.5">
                              Quantity Required
                            </label>
                            <input
                              type="text"
                              id="quantity"
                              name="quantity"
                              placeholder="e.g., 200 kg/week"
                              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            Message
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows={4}
                            maxLength={500}
                            placeholder="Tell us about your fresh produce requirements..."
                            className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                          ></textarea>
                          <p className="text-foreground-400 text-xs mt-1">Maximum 500 characters</p>
                        </div>

                        {error && (
                          <p className="text-red-500 text-sm text-center">{error}</p>
                        )}
                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          <i className={submitting ? 'ri-loader-4-line animate-spin text-lg' : 'ri-send-plane-fill text-lg'}></i>
                          {submitting ? 'Sending…' : 'Send Inquiry'}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>
        </section>

        {/* Map Section */}
        <section className="pb-14 md:pb-20 px-4 md:px-6 lg:px-10">
          <FadeInSection>
            <div className="max-w-7xl mx-auto">
              <div className="rounded-2xl overflow-hidden border border-background-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3469.2875429198766!2d77.2044!3d29.5394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390c3cd5f1e8e8e1%3A0x5a837af6c90f5fb5!2sKairana%2C%20Shamli%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1700000000000"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bassi Enterprises Location"
                  className="w-full"
                ></iframe>
              </div>
            </div>
          </FadeInSection>
        </section>
      </main>
      <Footer />
    </div>
  );
}